/**
 * LedgerView — Kronolojik tum hareketleri tek tabloda gosterir.
 * Run running balance hesaplar. Sade ve net.
 */
import React, { useState, useMemo } from 'react';
import { ArrowDownLeft, ArrowUpRight, FileText, Receipt, Search } from 'lucide-react';

const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function LedgerView({ entries }) {
    const [filter, setFilter] = useState('all'); // all | invoice | credit_note | payment_in | payment_out
    const [q, setQ] = useState('');

    const filtered = useMemo(() => {
        let list = entries || [];
        if (filter === 'payment_in') {
            list = list.filter(e => e.kind === 'payment' && e.direction === 'in');
        } else if (filter === 'payment_out') {
            list = list.filter(e => e.kind === 'payment' && e.direction === 'out');
        } else if (filter !== 'all') {
            list = list.filter(e => e.kind === filter);
        }
        if (q.trim()) {
            const qq = q.toLowerCase();
            list = list.filter(e => `${e.ref || ''} ${e.description || ''} ${(e.matched_invoice_numbers || []).join(' ')}`.toLowerCase().includes(qq));
        }
        return list;
    }, [entries, filter, q]);

    // Running balance hesabi + final toplam
    const withBalance = useMemo(() => {
        let bal = 0;
        return filtered.map(e => {
            bal += (e.signed_amount || 0);
            return { ...e, _balance: Math.round(bal * 100) / 100 };
        });
    }, [filtered]);

    const totals = useMemo(() => {
        let sumAmount = 0, sumSigned = 0;
        for (const e of withBalance) {
            sumAmount += Math.abs(e.amount || 0);
            sumSigned += e.signed_amount || 0;
        }
        return { sumAmount, sumSigned, finalBalance: withBalance.length ? withBalance[withBalance.length - 1]._balance : 0 };
    }, [withBalance]);

    if (!entries || entries.length === 0) {
        return <div className="p-12 text-center text-sm text-[#9CA3AF]">Defter bos</div>;
    }

    return (
        <div data-testid="ledger-view">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#F3F4F6] flex-wrap">
                <div className="flex gap-1.5 items-center">
                    <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mr-1">Tip:</span>
                    {[
                        ['all', 'Tumu', null],
                        ['invoice', 'Faturalar', FileText],
                        ['credit_note', 'Iade', Receipt],
                        ['payment_in', 'Tahsilat', ArrowDownLeft],
                        ['payment_out', 'Odeme', ArrowUpRight],
                    ].map(([k, l, Icon]) => (
                        <button key={k} onClick={() => setFilter(k)}
                            className={`px-3 py-1 rounded-lg text-[11px] font-semibold flex items-center gap-1 ${filter === k ? 'bg-[#111] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}
                            data-testid={`ledger-view-filter-${k}`}>
                            {Icon && <Icon size={11} />} {l}
                        </button>
                    ))}
                </div>
                <div className="relative w-56">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={q} onChange={e => setQ(e.target.value)}
                        placeholder="Ref, aciklama, fatura ara..."
                        className="w-full h-7 pl-6 pr-2 bg-white border border-[#E5E7EB] rounded text-[11px]"
                        data-testid="ledger-view-search" />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr className="text-[10px] text-[#9CA3AF] uppercase">
                            <th className="text-left px-3 py-2 font-semibold">Tarih</th>
                            <th className="text-left px-3 py-2 font-semibold">Tip</th>
                            <th className="text-left px-3 py-2 font-semibold">Ref</th>
                            <th className="text-left px-3 py-2 font-semibold">Aciklama</th>
                            <th className="text-right px-3 py-2 font-semibold">Tutar</th>
                            <th className="text-right px-3 py-2 font-semibold">Bakiye Etkisi</th>
                            <th className="text-center px-3 py-2 font-semibold">Durum</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                        {withBalance.map((e, i) => <EntryRow key={`${e.kind}-${e.doc_id}-${i}`} e={e} />)}
                    </tbody>
                    {withBalance.length > 0 && (
                        <tfoot className="bg-[#FAFAFA] border-t-2 border-[#111] sticky bottom-0" data-testid="ledger-view-totals">
                            <tr>
                                <td colSpan={4} className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam</td>
                                <td className="px-3 py-2.5 text-right text-sm font-bold text-[#111]" data-testid="ledger-view-total-amount">{fmt(totals.sumAmount)}</td>
                                <td className={`px-3 py-2.5 text-right text-sm font-bold ${totals.finalBalance >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="ledger-view-final-balance">
                                    {totals.finalBalance >= 0 ? '+' : ''}{fmt(totals.finalBalance)}
                                </td>
                                <td className="px-3 py-2.5"></td>
                            </tr>
                            <tr className="text-[10px] text-[#9CA3AF]">
                                <td colSpan={4} className="px-3 py-1 text-right uppercase tracking-wider">Nihai cari bakiye →</td>
                                <td colSpan={2} className="px-3 py-1 text-right italic">
                                    {totals.finalBalance >= 0 ? 'bize borclu' : 'biz borcluyuz'}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
            {filtered.length === 0 && <div className="p-8 text-center text-xs text-[#9CA3AF]">Filtreye uygun kayit yok</div>}
        </div>
    );
}

function EntryRow({ e }) {
    const isPay = e.kind === 'payment';
    const isCredit = e.kind === 'credit_note';
    const positive = (e.signed_amount || 0) >= 0;
    const tipBadge = (() => {
        if (e.kind === 'invoice' && e.direction === 'sales') return { l: 'Satis', cls: 'bg-emerald-50 text-emerald-700' };
        if (e.kind === 'invoice' && e.direction === 'purchase') return { l: 'Alis', cls: 'bg-blue-50 text-blue-700' };
        if (e.kind === 'credit_note' && e.direction === 'sales') return { l: 'S-IADE', cls: 'bg-rose-50 text-rose-700' };
        if (e.kind === 'credit_note' && e.direction === 'purchase') return { l: 'A-IADE', cls: 'bg-amber-50 text-amber-700' };
        if (e.kind === 'payment' && e.direction === 'in') return { l: 'Tahsilat', cls: 'bg-teal-50 text-teal-700' };
        if (e.kind === 'payment' && e.direction === 'out') return { l: 'Odeme', cls: 'bg-orange-50 text-orange-700' };
        if (e.kind === 'bank_fee') return { l: 'Komisyon', cls: 'bg-purple-100 text-purple-700' };
        return { l: e.kind, cls: 'bg-gray-100 text-gray-500' };
    })();
    const statusBadge = (() => {
        const s = e.status || '';
        const cfg = {
            paid: { l: 'Odendi', cls: 'bg-emerald-50 text-emerald-700' },
            open: { l: 'Acik', cls: 'bg-blue-50 text-blue-700' },
            partially_paid: { l: 'Kismi', cls: 'bg-amber-50 text-amber-700' },
            overdue: { l: 'Gecmis', cls: 'bg-red-50 text-red-700' },
            matched: { l: 'Eslesti', cls: 'bg-emerald-50 text-emerald-700' },
            unmatched: { l: 'Eslesmedi', cls: 'bg-gray-100 text-gray-600' },
            draft: { l: 'Taslak', cls: 'bg-gray-100 text-gray-500' },
        }[s] || { l: s, cls: 'bg-gray-100 text-gray-500' };
        return <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${cfg.cls}`}>{cfg.l}</span>;
    })();
    return (
        <tr className="hover:bg-[#F9FAFB]">
            <td className="px-3 py-2 text-[#6B7280] text-xs whitespace-nowrap">{e.date}</td>
            <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${tipBadge.cls}`}>{tipBadge.l}</span></td>
            <td className="px-3 py-2 text-xs font-medium text-[#111] max-w-[140px] truncate" title={e.ref}>
                {isPay ? (e.matched_invoice_numbers?.length > 0 ? '→ ' + e.matched_invoice_numbers.join(', ') : (e.ref || '-')) : (e.ref || '-')}
            </td>
            <td className="px-3 py-2 text-xs text-[#374151] max-w-[280px] truncate" title={e.description}>{e.description || '—'}</td>
            <td className={`px-3 py-2 text-right text-sm font-bold whitespace-nowrap ${isCredit ? 'text-rose-600' : 'text-[#111]'}`}>
                {isCredit ? '-' : ''}{fmt(e.amount)}
            </td>
            <td className={`px-3 py-2 text-right text-xs font-semibold whitespace-nowrap ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
                {positive ? '+' : ''}{fmt(e.signed_amount)}
            </td>
            <td className="px-3 py-2 text-center">{statusBadge}</td>
        </tr>
    );
}
